import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';
import { makeApi } from '../api/client';

export function ActivationCodeDialog({ storeId, open, onClose }: { storeId: string; open: boolean; onClose: () => void }) {
  const { getAccessToken } = useAuth();
  const api = makeApi(getAccessToken);
  const [now, setNow] = useState(Date.now());
  const mut = useMutation({ mutationFn: () => api.org.generateActivationCode({ path: { id: storeId } }) });

  useEffect(() => { if (open && !mut.data && !mut.isPending) mut.mutate(); }, [open]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  if (!open) return null;
  const code = mut.data?.data?.activation_code;
  const expiresAt = mut.data?.data ? new Date(mut.data.data.expires_at).getTime() : 0;
  const remainingMs = Math.max(0, expiresAt - now);
  const hh = String(Math.floor(remainingMs / 3_600_000)).padStart(2, '0');
  const mm = String(Math.floor((remainingMs % 3_600_000) / 60_000)).padStart(2, '0');
  const ss = String(Math.floor((remainingMs % 60_000) / 1000)).padStart(2, '0');

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded p-6 w-[28rem] space-y-4">
        <h2 className="text-lg font-bold text-primary">Activation code</h2>
        {mut.isPending && <p>Generating…</p>}
        {code && (
          <>
            <p className="font-mono text-3xl tracking-widest text-center bg-surface rounded py-4">{code}</p>
            <p className="text-sm text-accent font-semibold">This will not be shown again. Save it now.</p>
            <p className="text-sm text-ink/60">Expires in {hh}:{mm}:{ss}</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => void navigator.clipboard.writeText(code)} className="rounded bg-primary px-4 py-2 text-white">Copy</button>
              <a download={`dhuttu-activation-${storeId}.txt`} href={`data:text/plain;charset=utf-8,${encodeURIComponent(code)}`}
                className="rounded border border-primary px-4 py-2 text-primary">Download</a>
              <button type="button" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
